import React from 'react';
import PropTypes from 'prop-types';
import Form from 'react-bootstrap/Form';
import Col from 'react-bootstrap/Col';
import { Field, useFormikContext } from 'formik';
import { AutoComplete, DropdownSelect, ValidatedFormInputField } from 'ui';
import { countryList } from 'utils/constants/country';
import { getCountryNameByCode, getStateNameByCode } from 'utils/utilities';
import styles from '../../UserDetails.module.css';

const AddressSection = ({
    confirmUserInformation,
    source,
    disableField,
    addressBlocked,
    zipCodeBlocked,
    isUSA,
    conditionallyRequired,
    localValue,
    addressList,
    isAddressLoading,
    handleDropdownValueChange,
    handleDropdownValueSelect,
    getStatesOptions,
}) => {
    const { values, touched, errors } = useFormikContext();

    return (
        <>
            <p className={styles.sectionHeader}>Address Details</p>

            <Form.Row>
                <Col xs="12" style={{ marginBottom: '16px' }}>
                    <DropdownSelect
                        label={`Country${conditionallyRequired('country')}`}
                        name="country"
                        placeholder="Select or search for a country"
                        options={countryList}
                        value={localValue?.country || getCountryNameByCode(values?.country)}
                        onChange={handleDropdownValueChange}
                        onSelect={(item) => handleDropdownValueSelect('country', item)}
                        error={touched?.country && Boolean(errors?.country)}
                        helperText={touched?.country && errors?.country}
                        title="Select Country"
                        disabled={disableField || addressBlocked}
                        disableCustomValue
                    />
                </Col>
            </Form.Row>

            {isUSA && (
                <Form.Row className={styles.addressFormRow}>
                    <Col xs="12">
                        <AutoComplete
                            label={`Address${conditionallyRequired('address')}`}
                            name="address"
                            placeholder="Search for an address"
                            value={localValue?.address || values?.address}
                            options={addressList}
                            onChange={handleDropdownValueChange}
                            onSelect={(item) => handleDropdownValueSelect('address', item)}
                            error={touched?.address && Boolean(errors?.address)}
                            helperText={touched?.address && errors.address}
                            disabled={disableField || addressBlocked}
                            loading={isAddressLoading}
                            serverSideFiltering
                        />
                    </Col>
                </Form.Row>
            )}

            <Form.Row className={styles.formRow}>
                <Col md={6} xs={6}>
                    <DropdownSelect
                        label={`State${conditionallyRequired('state')}`}
                        name="state"
                        placeholder="Select or search for a state"
                        options={getStatesOptions() || []}
                        value={
                            localValue?.state || getStateNameByCode(values?.state, values?.country)
                        }
                        onChange={handleDropdownValueChange}
                        title="Select State"
                        onSelect={(item) => handleDropdownValueSelect('state', item)}
                        error={touched.state && Boolean(errors.state)}
                        helperText={touched.state && errors.state}
                        disabled={!values?.country || disableField || addressBlocked}
                    />
                </Col>
                <Col md={6} xs={6}>
                    <Form.Label className={styles.formLabel}>
                        City{conditionallyRequired('city')}
                    </Form.Label>
                    <Field
                        component={ValidatedFormInputField}
                        type="text"
                        variant="outlined"
                        name="city"
                        placeholder="Enter city name"
                        confirmUserInformation={confirmUserInformation}
                        source={source}
                        disabled={disableField || addressBlocked}
                    />
                </Col>
            </Form.Row>

            <Form.Row className={styles.formRow}>
                <Col md={6} xs={6}>
                    <Form.Label className={styles.formLabel}>
                        House No{conditionallyRequired('houseNumber')}
                    </Form.Label>
                    <Field
                        component={ValidatedFormInputField}
                        type="text"
                        variant="outlined"
                        name="houseNumber"
                        placeholder="Enter house number"
                        confirmUserInformation={confirmUserInformation}
                        source={source}
                        disabled={disableField || addressBlocked}
                    />
                </Col>
                <Col md={6} xs={6}>
                    <Form.Label className={styles.formLabel}>
                        Street Name{conditionallyRequired('streetName')}
                    </Form.Label>
                    <Field
                        component={ValidatedFormInputField}
                        type="text"
                        variant="outlined"
                        name="streetName"
                        placeholder="Enter street name"
                        confirmUserInformation={confirmUserInformation}
                        source={source}
                        disabled={disableField || addressBlocked}
                    />
                </Col>
            </Form.Row>
            <Form.Row className={styles.formRow}>
                <Col md={6} xs={6}>
                    <Form.Label className={styles.formLabel}>
                        Zip Code{conditionallyRequired('zipCode')}
                    </Form.Label>
                    <Field
                        component={ValidatedFormInputField}
                        type="text"
                        variant="outlined"
                        name="zipCode"
                        placeholder="Enter zip code"
                        autoComplete="new-password"
                        confirmUserInformation={confirmUserInformation}
                        source={source}
                        disabled={disableField || (addressBlocked && zipCodeBlocked)}
                    />
                </Col>
                <Col md={6} xs={6}>
                    <Form.Label className={styles.formLabel}>Apartment Number</Form.Label>
                    <Field
                        component={ValidatedFormInputField}
                        type="text"
                        variant="outlined"
                        name="apartmentNumber"
                        placeholder="Enter apartment number"
                        confirmUserInformation={confirmUserInformation}
                        source={source}
                        disabled={disableField || addressBlocked}
                    />
                </Col>
            </Form.Row>
        </>
    );
};

AddressSection.propTypes = {
    confirmUserInformation: PropTypes.bool,
    source: PropTypes.string,
    disableField: PropTypes.any,
    addressBlocked: PropTypes.bool,
    zipCodeBlocked: PropTypes.bool,
    isUSA: PropTypes.bool,
    conditionallyRequired: PropTypes.func,
    localValue: PropTypes.object,
    addressList: PropTypes.array,
    isAddressLoading: PropTypes.bool,
    handleDropdownValueChange: PropTypes.func,
    handleDropdownValueSelect: PropTypes.func,
    getStatesOptions: PropTypes.func,
};

export default AddressSection;
